import qrcode from "qrcode-generator";
import type { Alarm, ChecklistCategory, ChecklistResult, ChecklistTemplate } from "@fts/core";
import { useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { Icon } from "../components/Icon";
import { Note, Panel } from "../components/ui";
import { useI18n, type TFn } from "../i18n";
import { formatAlarmTime } from "../lib/alarms";

/** Shared pieces of the checklist screens (ADR 0016). */

export const RESULT_TONE: Record<ChecklistResult, string> = { ok: "ok", ok_with_notes: "warn", maintenance_required: "crit" };

export function ResultBadge({ result }: { result: ChecklistResult }) {
  const { t } = useI18n();
  return <span className={`badge tone-${RESULT_TONE[result]}`}>{t(`checklist.result.${result}`)}</span>;
}

/** System templates are named by their category in the UI language; tenant templates by their own name. */
export function templateName(t: TFn, tpl: Pick<ChecklistTemplate, "name" | "systemCategory">): string {
  return tpl.systemCategory ? t("checklist.systemName", { category: t(`checklist.category.${tpl.systemCategory as ChecklistCategory}`) }) : tpl.name;
}

/** Item text in the UI language (TR when no EN text was entered). */
export const itemText = (lang: string, i: { textTr: string; textEn: string }) => (lang === "en" && i.textEn.trim() ? i.textEn : i.textTr);

/** The form URL printed into the QR code: hash route of the console, no login needed. */
export function checklistUrl(token: string, loc: Pick<Location, "origin" | "pathname"> = window.location): string {
  return `${loc.origin}${loc.pathname.replace(/[^/]*$/, "")}#/c/${token}`;
}

/** QR code as SVG rectangles (no innerHTML). Error correction M survives a scratched label. */
export function QrCode({ value, size = 160, label }: { value: string; size?: number; label: string }) {
  const cells = useMemo(() => {
    const qr = qrcode(0, "M");
    qr.addData(value);
    qr.make();
    const n = qr.getModuleCount();
    const dark: Array<[number, number]> = [];
    for (let r = 0; r < n; r++) for (let c = 0; c < n; c++) if (qr.isDark(r, c)) dark.push([c, r]);
    return { n, dark };
  }, [value]);
  const quiet = 4;
  const total = cells.n + quiet * 2;
  return (
    <svg className="qr" width={size} height={size} viewBox={`0 0 ${total} ${total}`} role="img" aria-label={label} shapeRendering="crispEdges">
      <rect className="qr-bg" width={total} height={total} />
      {cells.dark.map(([x, y]) => (
        <rect key={`${x}-${y}`} className="qr-dot" x={x + quiet} y={y + quiet} width={1} height={1} />
      ))}
    </svg>
  );
}

export type LabelEntry = { fleetNo: string; siteName: string; token: string };

/** One printable label: QR, fleet number, short instructions (sample text the customer may adapt). */
export function ChecklistLabel({ entry }: { entry: LabelEntry }) {
  const { t } = useI18n();
  const url = checklistUrl(entry.token);
  return (
    <div className="qr-label">
      <QrCode value={url} size={150} label={t("checklistQr.title")} />
      <div className="qr-label-text">
        <div className="qr-label-title">{t("checklistQr.labelTitle")}</div>
        <div className="qr-label-fleet mono">{entry.fleetNo}</div>
        <div className="qr-label-site">{entry.siteName}</div>
        <p className="qr-label-hint">{t("checklistQr.labelText")}</p>
      </div>
    </div>
  );
}

/**
 * Label sheet for printing: rendered into its own body-level node; print CSS hides everything else.
 * Calls window.print() once mounted (when available) and closes on the button.
 */
export function LabelSheet({ title, entries, onClose }: { title: string; entries: LabelEntry[]; onClose: () => void }) {
  const { t } = useI18n();
  useEffect(() => {
    document.body.classList.add("printing-labels");
    const h = setTimeout(() => {
      try {
        if (typeof window.print === "function") window.print();
      } catch {
        // Not available (tests, embedded browsers): the sheet stays on screen.
      }
    }, 50);
    return () => {
      clearTimeout(h);
      document.body.classList.remove("printing-labels");
    };
  }, []);
  return createPortal(
    <div className="print-area" role="dialog" aria-modal="true" aria-label={title}>
      <div className="print-toolbar">
        <strong>{title}</strong>
        <span className="muted small">{t("checklistQr.labelSample")}</span>
        <button type="button" className="button button-secondary button-sm" onClick={() => window.print?.()}>
          <Icon name="download" size={15} />
          {t("checklistQr.print")}
        </button>
        <button type="button" className="button button-secondary button-sm" onClick={onClose} data-autofocus>
          {t("checklistQr.close")}
        </button>
      </div>
      <div className="qr-sheet">
        {entries.map((e) => (
          <ChecklistLabel key={e.token} entry={e} />
        ))}
      </div>
    </div>,
    document.body,
  );
}

/** On a checklist alarm: shift, the no-lock note and a link to the vehicle's checklist tab. */
export function AlarmChecklistPanel({ alarm }: { alarm: Alarm }) {
  const { t, locale } = useI18n();
  return (
    <Panel title={t("alarmChecklist.title")}>
      <dl className="dl">
        {alarm.shiftName && (
          <div className="dl-row">
            <dt>{t("alarmChecklist.shift")}</dt>
            <dd>
              {alarm.shiftName}
              {alarm.shiftStart ? ` · ${formatAlarmTime(alarm.shiftStart, locale)}` : ""}
            </dd>
          </div>
        )}
      </dl>
      {alarm.type === "checklist_missed" && <p className="panel-text">{t("alarmChecklist.missedText")}</p>}
      <Note>{t("alarmChecklist.noLock")}</Note>
      {alarm.vehicleId && (
        <p>
          <a className="button button-secondary button-sm" href={`#/vehicles/${encodeURIComponent(alarm.vehicleId)}/checklist`}>
            {t("alarmChecklist.open")}
          </a>
        </p>
      )}
    </Panel>
  );
}
